import React, { useEffect, useRef, useState } from 'react';
import logo from '../assets/footer-logo.png';
import fbIcon from '../assets/contact-us/facebook-app-symbol.png';
import instaIcon from '../assets/contact-us/instagram.png';
import linkedinIcon from '../assets/contact-us/linkedin.png';
import { Link } from 'react-router-dom';

const Footer = () => {
  const [inView, setInView] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const sectionRef = useRef(null);


  // Define social media links with their URLs
  const socialLinks = [
    {
      name: 'Facebook',
      icon: fbIcon,
      url: 'https://www.facebook.com/share/16VL31FUML/?mibextid=wwXIfr'
    },
    {
      name: 'Instagram',
      icon: instaIcon,
      url: 'https://www.instagram.com/bigblue_inc?igsh=bXBkMnBjZzlwNWFi&utm_source=qr'
    },
    {
      name: 'Linkedin',
      icon: linkedinIcon,
      url: 'https://www.linkedin.com/company/big-blue-inc/'
    },
  ];

  // Define service links with IDs
  const serviceLinks = [
    { name: 'Air Freight', id: 'air-freight' },
    { name: 'Ocean Freight', id: 'ocean-freight' },
    { name: 'Road Freight', id: 'road-freight' },
    { name: 'Warehousing', id: 'warehousing-inventory' },
    { name: 'Customs Clearance', id: 'customs-compliance' },
  ];

  // Define quick links with their routes
  const quickLinks = [
    { name: 'Home', route: '/' },
    { name: 'About Us', route: '/About' },
    { name: 'Services', route: '/Services' },
    { name: 'Blogs', route: '/Blogs' },
    { name: 'Contact Us', route: '/Contact' },
  ];


  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      window.removeEventListener('resize', checkMobile);
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <footer
      ref={sectionRef}
      className="bg-[#03044E] text-[#f0f0f0] pt-12 pb-6 px-6 sm:px-12 lg:px-20 font-sans text-sm sm:text-base"
    >
      <style>
        {`
          @keyframes slideUp {
            from { 
              opacity: 0;
              transform: translateY(30px);
            }
            to { 
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          @keyframes slideLeft {
            from { 
              opacity: 0;
              transform: translateX(-100px);
            }
            to { 
              opacity: 1;
              transform: translateX(0);
            }
          }
          
          @keyframes slideRight {
            from { 
              opacity: 0;
              transform: translateX(100px);
            }
            to { 
              opacity: 1;
              transform: translateX(0);
            }
          }
          
          .animate-slide-up {
            animation: slideUp 0.8s ease-out forwards;
          }
          
          .animate-slide-left {
            animation: slideLeft 0.8s ease-out forwards;
          }
          
          .animate-slide-right {
            animation: slideRight 0.8s ease-out forwards;
          }
        `}
      </style>

      {/* Mobile Layout */}
      <div className={`md:hidden flex flex-col items-center text-center mb-8 ${inView ? 'opacity-100' : 'opacity-0'
        }`}>
        <img
          src={logo}
          alt="Big Blue Inc."
          className={`h-32 mb-8 ${inView ? 'animate-slide-up' : ''}`}
          style={{ animationDelay: '0ms' }}
        />

        {/* Quick Links */}
        <div className={`mb-8 ${inView ? 'animate-slide-up' : ''}`} style={{ animationDelay: '100ms' }}>
          <h3 className="text-white font-bold text-lg mb-4">Quick Links</h3>
          <ul className="space-y-3">
            {quickLinks.map((link, index) => (
              <li key={index}>
                <Link
                  to={link.route}
                  className="text-inherit bg-transparent hover:text-[#00cfff] hover:underline focus:outline-none block"
                >
                  {link.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Services */}
        <div className={`mb-8 ${inView ? 'animate-slide-up' : ''}`} style={{ animationDelay: '200ms' }}>
          <h3 className="text-white font-bold text-lg mb-4">Services</h3>
          <ul className="space-y-3">
            {serviceLinks.map((service, index) => (
              <li key={index}>
                <Link
                  to={`/Services#${service.id}`}
                  className="text-inherit bg-transparent hover:text-[#00cfff] focus:outline-none"
                >
                  {service.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Social Media */}
        <div className={`mb-4 ${inView ? 'animate-slide-up' : ''}`} style={{ animationDelay: '300ms' }}>
          <h3 className="text-white font-bold text-lg mb-4">Social Media</h3>
          <ul className="space-y-3 flex flex-col items-center">
            {socialLinks.map(({ name, icon, url }, index) => (
              <li key={index} className="flex items-center gap-3">
                <img src={icon} alt={name} className="w-5 h-5" />
                <a 
                  href={url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-transparent hover:text-[#00cfff] focus:outline-none"
                >
                  {name}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Desktop Layout */}
      <div className={`hidden md:grid grid-cols-1 md:grid-cols-[35%_21.66%_21.66%_21.66%] gap-8 mb-8 items-start ${inView ? 'opacity-100' : 'opacity-0'
        }`}>
        {/* Column 1: Logo */}
        <div className={`flex flex-col justify-start items-start ${inView ? 'animate-slide-left' : ''
          }`} style={{ animationDelay: '0ms' }}>
          <img src={logo} alt="Big Blue Inc." className="h-24 sm:h-56 lg:h-60 mb-2" />
        </div>

        {/* Quick Links */}
        <div className={`flex flex-col ${inView ? 'animate-slide-right' : ''}`} style={{ animationDelay: '200ms' }}>
          <h3 className="text-white font-bold text-lg mb-4">Quick Links</h3>
          <ul className="space-y-3">
            {quickLinks.map((link, index) => (
              <li key={index}>
                <Link
                  to={link.route}
                  className="text-left w-full text-inherit bg-transparent hover:text-[#00cfff] hover:underline-none focus:outline-none block"
                >
                  {link.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Services */}
        <div className={`flex flex-col ${inView ? 'animate-slide-right' : ''
          }`} style={{ animationDelay: '400ms' }}>
          <h3 className="text-white font-bold text-lg mb-4">Services</h3>
          <ul className="space-y-3">
            {serviceLinks.map((service, index) => (
              <li key={index}>
                <Link
                  to={`/Services#${service.id}`}
                  className="text-left w-full text-inherit bg-transparent hover:text-[#00cfff] focus:outline-none"
                >
                  {service.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Social Media */}
        <div className={`flex flex-col ${inView ? 'animate-slide-right' : ''
          }`} style={{ animationDelay: '600ms' }}>
          <h3 className="text-white font-bold text-lg mb-4">Social Media</h3>
          <ul className="space-y-3">
            {socialLinks.map(({ name, icon, url }, index) => (
              <li key={index} className="flex items-center gap-3">
                <img src={icon} alt={name} className="w-5 h-5" />
                <a 
                  href={url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-left bg-transparent hover:text-[#00cfff] focus:outline-none"
                >
                  {name}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Bottom Bar (common for both layouts) */}
      <div
        className={`border-t border-[#00cfff] pt-4 flex flex-col sm:flex-row justify-between items-center text-sm text-[#f0f0f0] px-4 sm:px-10 ${inView ? 'opacity-100 animate-slide-up' : 'opacity-0'
          }`}
        style={{ animationDelay: isMobile ? '400ms' : '800ms' }}
      >
        <p className="mb-3 sm:mb-0 text-center">Big Blue Inc. – All rights reserved</p>
        <div className="flex items-center space-x-3">
          <a
            href="https://dynexisinc.com"
            className="hover:text-[#7CFC00]"
            target="_blank"
            rel="noopener noreferrer"
          >
            Powered by Dynexis.
          </a>
        </div>
      </div>
    </footer>
  );
};

export default Footer;